/**
 * PollingService — Periodic sync for email, messages, etc.
 *
 * Configurable sources with per-source intervals (default 15s).
 * Deduplicates via lastSeenId per source.
 * On new item → eventBus.emit({ type: 'email.received' | 'message.received', ... })
 * Error in one source does not block others.
 */

import type { EngineEvent, EngineEventType, PollingSource } from './types';
import { eventBus } from './eventBus';

/** Default polling interval (15 seconds) */
const DEFAULT_INTERVAL_MS = 15000;

/** Polling handler function for a source type */
type PollingHandler = (source: PollingSource) => Promise<PolledItem[]>;

/** A single item returned by a polling handler */
export interface PolledItem {
  /** Unique item ID for deduplication */
  id: string;
  /** Event type to emit */
  eventType: EngineEventType;
  /** Event data payload */
  data: Record<string, unknown>;
  /** Entity type */
  entityType?: string;
}

/** Registry of polling handlers by source type */
const pollingHandlers: Map<string, PollingHandler> = new Map();

/**
 * Register a polling handler for a source type
 */
export function registerPollingHandler(sourceType: string, handler: PollingHandler): void {
  pollingHandlers.set(sourceType, handler);
}

class PollingService {
  private sources: Map<string, PollingSource> = new Map();
  private timers: Map<string, ReturnType<typeof setInterval>> = new Map();
  private running = false;

  /**
   * Add a polling source
   */
  addSource(source: PollingSource): void {
    this.sources.set(source.id, source);

    // If already running, start polling this source immediately
    if (this.running && source.enabled) {
      this.startSourcePolling(source);
    }
  }

  /**
   * Remove a polling source
   */
  removeSource(id: string): void {
    this.stopSourcePolling(id);
    this.sources.delete(id);
  }

  /**
   * Enable/disable a source
   */
  setSourceEnabled(id: string, enabled: boolean): void {
    const source = this.sources.get(id);
    if (!source) return;

    source.enabled = enabled;
    if (enabled && this.running) {
      this.startSourcePolling(source);
    } else if (!enabled) {
      this.stopSourcePolling(id);
    }
  }

  /**
   * Start all enabled polling sources
   */
  start(): void {
    if (this.running) return;
    this.running = true;

    for (const source of this.sources.values()) {
      if (source.enabled) {
        this.startSourcePolling(source);
      }
    }
  }

  /**
   * Stop all polling
   */
  stop(): void {
    this.running = false;
    for (const [id] of this.timers) {
      this.stopSourcePolling(id);
    }
  }

  /**
   * Pause all polling (keeps state, stops timers)
   */
  pause(): void {
    for (const [id] of this.timers) {
      this.stopSourcePolling(id);
    }
  }

  /**
   * Resume all previously running sources
   */
  resume(): void {
    if (!this.running) return;
    for (const source of this.sources.values()) {
      if (source.enabled && !this.timers.has(source.id)) {
        this.startSourcePolling(source);
      }
    }
  }

  /**
   * Get all registered sources
   */
  getSources(): PollingSource[] {
    return Array.from(this.sources.values());
  }

  /**
   * Check if the service is running
   */
  isRunning(): boolean {
    return this.running;
  }

  /**
   * Force poll a specific source immediately
   */
  async pollNow(sourceId: string): Promise<void> {
    const source = this.sources.get(sourceId);
    if (!source) return;
    await this.pollSource(source);
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private startSourcePolling(source: PollingSource): void {
    // Clear existing timer if any
    this.stopSourcePolling(source.id);

    const intervalMs = source.intervalMs || DEFAULT_INTERVAL_MS;

    // Initial poll
    this.pollSource(source).catch((err) => {
      console.error(`[PollingService] Initial poll error for "${source.name}":`, err);
    });

    // Set up interval
    const timer = setInterval(() => {
      this.pollSource(source).catch((err) => {
        console.error(`[PollingService] Poll error for "${source.name}":`, err);
      });
    }, intervalMs);

    this.timers.set(source.id, timer);
  }

  private stopSourcePolling(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(id);
    }
  }

  private async pollSource(source: PollingSource): Promise<void> {
    const handler = pollingHandlers.get(source.type);
    if (!handler) {
      console.warn(`[PollingService] No handler for source type: "${source.type}"`);
      return;
    }

    try {
      const items = await handler(source);

      // Deduplicate: only emit items newer than lastSeenId
      let latestId = source.lastSeenId;

      for (const item of items) {
        // Skip already-seen items
        if (source.lastSeenId && item.id <= source.lastSeenId) {
          continue;
        }

        // Emit event
        const event: EngineEvent = {
          id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: item.eventType,
          entityType: item.entityType as EngineEvent['entityType'],
          entityId: item.id,
          data: item.data,
          timestamp: new Date().toISOString(),
          userId: 'system',
          source: 'polling',
        };

        await eventBus.emit(event);

        // Track latest ID
        if (!latestId || item.id > latestId) {
          latestId = item.id;
        }
      }

      // Update source state
      source.lastPollAt = new Date().toISOString();
      if (latestId) {
        source.lastSeenId = latestId;
      }
    } catch (err) {
      console.error(`[PollingService] Error polling "${source.name}":`, err);
      // Error in one source does not block others
    }
  }
}

/**
 * Singleton PollingService instance
 */
export const pollingService = new PollingService();
